/**
 * MealEngine.js
 *
 * Stand-in for the real UC-02/UC-03/UC-04 Rule Engine + AI Recommendation
 * Engine. Reads the same signals the assessment already collects — diet
 * type, cuisine, medical conditions, dislikes/likes, calorie/macro
 * targets — and uses them to actually pick and size meals, instead of
 * returning the same fixed dishes to everyone.
 *
 * Still a mock: it's a small hand-authored catalog (MealCatalog.js) and
 * simple filter/score/scale logic, not a real nutrition database or ML
 * ranker. But every decision it makes is now genuinely driven by the
 * user's own answers, so two different profiles produce two different
 * plans.
 *
 * Self-contained by design (see the note in SummaryStep.helpers.js) plus
 * one same-folder import of LoadingScreen.helpers.js's calculation
 * primitives, which this reuses rather than duplicates.
 */

import { MEAL_CATALOG, MEAL_SLOT_ORDER } from "./MealCatalog";
// Adjust this path if MealCatalog.js/MealEngine.js end up somewhere other
// than a sibling folder to Loading/ (e.g. src/components/sections/MealEngine/,
// alongside Loading/, DietPlan/, Assesment/, etc.).
import {
  calculateBmi,
  calculateBmr,
  calculateTdee,
  calculateCalorieTarget,
  calculateProteinTarget,
  calculateWaterTarget,
} from "../Loading/LoadingScreen.helpers";

// Normalizes an id/label for comparison: "Sweet Potato" / "sweet-potato" /
// "sweet_potato" should all match the same underlying food. Mirrors the
// slugify() convention DislikesStep.js already uses for custom entries.
export function normalizeFoodId(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// UC-08-style check: pregnancy + gestational diabetes is the one
// real-data risk case the mock has always been able to check (Screen 11).
// Combined here with MedicalInfoStep's own already-computed riskFlag
// (multiple caution/professional_review conditions, or any
// professional_review condition — see MedicalInfoStep.js) rather than
// re-deriving condition tiers a second time.
export function computeRiskFlag(payload) {
  const medicalRiskFlag = Boolean(payload?.medical?.riskFlag);
  const isPregnantWithGDM =
    Array.isArray(payload?.womensHealth?.conditions) &&
    payload.womensHealth.conditions.includes("pregnant") &&
    payload.womensHealth.gestationalDiabetes === "yes";
  return medicalRiskFlag || isPregnantWithGDM;
}

/**
 * Derives the filter/ranking context every selection, scaling, regenerate,
 * and tip-building function below takes as input. Computed once per plan
 * (or once per DietPlanScreen mount, for regenerate/replace) from the
 * same payload LoadingScreen already assembles — see buildMockPlan in
 * LoadingScreen.js and the `mealContext` prop threaded into
 * DietPlanScreen.js.
 */
export function buildMealContext(payload) {
  const conditions = Array.isArray(payload?.medical?.conditions) ? payload.medical.conditions : [];
  const dislikes = Array.isArray(payload?.dislikes?.dislikes) ? payload.dislikes.dislikes : [];
  const likes = Array.isArray(payload?.likes?.likes) ? payload.likes.likes : [];
  const cuisines = Array.isArray(payload?.foodPreferences?.cuisines) ? payload.foodPreferences.cuisines : [];

  return {
    // Diet Type is FoodPreferencesStep's one HARD database-level filter
    // (FSD v2.1 §6) — defaults to "vegetarian" (the least restrictive
    // assumption that still excludes meat) if somehow unset, rather than
    // guessing "non_vegetarian" and risking a real dietary violation.
    dietType: payload?.foodPreferences?.dietType || "vegetarian",
    cuisineSet: new Set(cuisines),
    likeSet: new Set(likes.map(normalizeFoodId)),
    dislikeSet: new Set(dislikes.map(normalizeFoodId)),
    // DEC-09's one hard ingredient-level exclusion outside Dislikes.
    glutenRestricted: conditions.includes("coeliac_gluten"),
    // DEC-17: no personalized calorie-deficit/weight-loss plan.
    planBlocked: Boolean(payload?.medical?.planBlocked),
    conditions,
    goal: payload?.goal ?? null,
    fastingWindow: payload?.mealSchedule?.fastingWindow || null,
    riskFlag: computeRiskFlag(payload),
  };
}

// Hard-filter: diet type, gluten (if flagged), and dislikes. A dish that
// fails any of these must never reach the candidate pool — same
// "hard-exclude before the AI Recommendation Layer ever sees it" rule
// DislikesStep.js documents for allergens/dislikes.
export function isMealEligible(meal, ctx, { ignoreDislikes = false } = {}) {
  // A falsy ctx.dietType means "unknown," not "no valid diet type" — skip
  // this filter rather than rejecting every dish, so a defensive/partial
  // ctx (e.g. DietPlanScreen_helpers.js's mocks called without the real
  // mealContext prop) degrades to "less filtered" instead of "no results."
  if (ctx?.dietType && !meal.dietTypes.includes(ctx.dietType)) return false;
  if (ctx?.glutenRestricted && !meal.glutenFree) return false;
  if (!ignoreDislikes && ctx?.dislikeSet && ctx.dislikeSet.size > 0) {
    const hasDislikedIngredient = meal.ingredients.some((ing) => ctx.dislikeSet.has(normalizeFoodId(ing.id)));
    if (hasDislikedIngredient) return false;
  }
  return true;
}

// Soft ranking: cuisine match (FSD v2.1 §6 soft preference) plus a small
// boost per ingredient the user explicitly liked on Screen 8.
export function scoreMeal(meal, ctx) {
  let score = 0;
  if (ctx?.cuisineSet && meal.cuisineTags.some((c) => ctx.cuisineSet.has(c))) score += 3;
  if (ctx?.likeSet) {
    score += meal.ingredients.filter((ing) => ctx.likeSet.has(normalizeFoodId(ing.id))).length;
  }
  return score;
}

function rankedCandidates(slotId, ctx, excludeIds, ignoreDislikes) {
  return (MEAL_CATALOG[slotId] || [])
    .filter((meal) => !excludeIds.has(meal.id) && isMealEligible(meal, ctx, { ignoreDislikes }))
    .sort((a, b) => scoreMeal(b, ctx) - scoreMeal(a, ctx) || a.id.localeCompare(b.id));
}

/**
 * Picks the single best eligible dish for one slot. Falls back to
 * ignoring dislikes (never diet type or gluten — those are non-negotiable
 * safety/compliance filters) only if literally nothing else qualifies, so
 * a slot is never silently dropped over a soft-exclusion technicality.
 */
export function pickMealForSlot(slotId, ctx, excludeIds = new Set()) {
  const strict = rankedCandidates(slotId, ctx, excludeIds, false);
  if (strict.length > 0) return strict[0];

  const relaxed = rankedCandidates(slotId, ctx, excludeIds, true);
  if (relaxed.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[MealEngine] No dislike-clean dish available for "${slotId}" (dietType="${ctx?.dietType}") — ` +
        "falling back to a dish that contains a disliked ingredient. Catalog needs more coverage for this combination."
    );
    return relaxed[0];
  }
  return null;
}

// Builds { slotId: catalogMeal } for every requested slot, in order.
// Slots with truly no eligible dish (shouldn't normally happen — see
// pickMealForSlot's fallback) are omitted rather than filled with an
// ineligible dish.
export function pickMealsForSlots(slotIds, ctx) {
  const picked = {};
  for (const slotId of slotIds) {
    const meal = pickMealForSlot(slotId, ctx);
    if (!meal) {
      // eslint-disable-next-line no-console
      console.warn(`[MealEngine] No eligible dish found for slot "${slotId}" — dropping it from the plan.`);
      continue;
    }
    picked[slotId] = meal;
  }
  return picked;
}

// Rough share of daily calories/protein each slot should carry. Only
// covers the 3 mandatory + 3 optional slots MealCatalog.js defines;
// normalized against whichever slots are actually present so the
// fractions always sum to 1 regardless of how many meals the user chose.
const SLOT_WEIGHTS = {
  breakfast: 0.25,
  mid_morning_snack: 0.08,
  lunch: 0.32,
  afternoon_snack: 0.08,
  dinner: 0.27,
  bedtime_snack: 0.08,
};

export function computeSlotTargets(slotIds, totalCalories, totalProteinG) {
  const weights = slotIds.map((id) => SLOT_WEIGHTS[id] ?? 0.15);
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
  const targets = {};
  slotIds.forEach((slotId, i) => {
    const fraction = weights[i] / weightSum;
    targets[slotId] = {
      calories: totalCalories ? Math.round((totalCalories * fraction) / 10) * 10 : null,
      proteinG: totalProteinG ? Math.round(totalProteinG * fraction) : null,
    };
  });
  return targets;
}

// Scales a catalog dish's fixed macros toward a per-slot target, clamped
// so a very high/low target can't stretch a dish into an absurd portion
// size (e.g. a 3000-calorie "Fruit Chaat"). This is a mock stand-in for
// real portion-scaling logic, not a nutrition-accurate recalculation.
const MIN_SCALE = 0.6;
const MAX_SCALE = 1.6;

export function buildScaledMealSlot(slotId, catalogMeal, target, scheduledTime) {
  const rawFactor = target?.calories && catalogMeal.calories ? target.calories / catalogMeal.calories : 1;
  const factor = Math.min(MAX_SCALE, Math.max(MIN_SCALE, rawFactor));

  return {
    // `id` stays the slot id — DietPlanScreen.js keys its React state
    // (meals, mealRegenCounts, mealNotice, mealBusy) by meal.id, so this
    // must not change even though the underlying dish can.
    id: slotId,
    // `dishId` is new — the specific catalog entry actually chosen, kept
    // separately so regenerate (below) can exclude "the dish already
    // shown" without touching the slot id contract above.
    dishId: catalogMeal.id,
    slotLabel: catalogMeal.slotLabel,
    time: scheduledTime || catalogMeal.defaultTime,
    name: catalogMeal.name,
    calories: Math.round((catalogMeal.calories * factor) / 5) * 5,
    proteinG: Math.max(1, Math.round(catalogMeal.proteinG * factor)),
    carbsG: Math.max(1, Math.round(catalogMeal.carbsG * factor)),
    fatG: Math.max(1, Math.round(catalogMeal.fatG * factor)),
    ingredients: catalogMeal.ingredients,
    recipeSteps: catalogMeal.recipeSteps,
  };
}

/**
 * Health Summary, but DEC-17-aware: a planBlocked profile (currently just
 * "eating_disorder_concern") must not receive a calorie-deficit/
 * weight-loss target (§5). Swaps in "maintain_weight" (plain TDEE, no
 * adjustment) for both the displayed summary and the calorie figure meal
 * selection actually scales against, so the two can never disagree the
 * way they would if only one of them respected planBlocked.
 */
export function buildEffectiveHealthSummary(payload) {
  const { weightKg, heightCm, age, gender } = payload?.basicInfo ?? {};
  const bmi = calculateBmi(weightKg, heightCm);
  const bmr = calculateBmr(weightKg, heightCm, age, gender);
  const tdee = calculateTdee(bmr, payload?.activity);
  const planBlocked = Boolean(payload?.medical?.planBlocked);
  const effectiveGoal = planBlocked ? "maintain_weight" : payload?.goal;
  const calories = calculateCalorieTarget(tdee, effectiveGoal);
  const protein = calculateProteinTarget(weightKg, effectiveGoal);
  const water = calculateWaterTarget(weightKg);

  return {
    calories: calories ? Math.round(calories / 10) * 10 : null,
    proteinG: protein ? Math.round(protein) : null,
    waterL: water ? Math.round(water * 10) / 10 : null,
    bmi: bmi ? Math.round(bmi * 10) / 10 : null,
    goal: payload?.goal ? String(payload.goal).replace(/_/g, " ") : "—",
    planBlocked,
  };
}

// Builds each day's tips from the same signals driving meal selection —
// goal, conditions, fasting window, plan-blocked status — instead of a
// fixed 3-line list shown to everyone. Ordered safety-relevant-first and
// capped so the list stays scannable.
export function buildDailyTips(ctx) {
  const tips = [];

  if (ctx.planBlocked) {
    tips.push(
      "This plan is built for balanced nutrition, not calorie restriction — please check in with a doctor or registered dietitian before making changes."
    );
  }
  if (ctx.riskFlag) {
    tips.push(
      "You've flagged conditions that need extra care together — we'd recommend confirming this plan with your doctor before starting."
    );
  }
  if (ctx.conditions.includes("diabetes")) {
    tips.push("Favor low-glycemic-index foods and pair carbs with protein or fiber to help blunt blood sugar spikes.");
  }
  if (ctx.conditions.includes("hypertension")) {
    tips.push("Go easy on added salt, salty snacks, and pickles.");
  }
  if (ctx.conditions.includes("gerd_reflux")) {
    tips.push("Avoid lying down right after meals, and go easy on very spicy or fried food.");
  }
  if (ctx.conditions.includes("kidney_disease")) {
    tips.push("Some foods here may need adjusting for kidney-friendly potassium/phosphorus limits — please review with your doctor.");
  }
  if (!ctx.planBlocked && ctx.goal === "lose_weight") {
    tips.push("You're in a mild calorie deficit — aim for slow, steady progress rather than a fast cut.");
  }
  if (ctx.goal === "muscle_gain" || ctx.goal === "gain_weight") {
    tips.push("Hit your protein target consistently, and pair this plan with regular resistance training.");
  }
  if (ctx.fastingWindow) {
    tips.push("Your fasting window is set, so we've left out the bedtime snack — keep sipping water during the fasting hours.");
  } else {
    tips.push("Try to keep at least a 2-hour gap between dinner and bedtime.");
  }
  tips.push("Drink a glass of water before each meal — it helps with portion control.");
  tips.push("Add a source of protein to every meal to help you stay full longer.");

  return tips.slice(0, 5);
}

export { MEAL_CATALOG, MEAL_SLOT_ORDER };
